import React from 'react';

import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import { useRefValue } from '@looko-corp/acloset-react-kit/src/react';
import { acCaptureException } from '@looko-corp/acloset-react-kit/src/sentry';
import { uuidv4 } from '@looko-corp/acloset-react-kit/src/utils-ext';

import { analyticsService } from '@/app/analytics';
import { useLogoutUserAuth, useSetUserAuth, useUserAuth } from '@/state/cookieContext';

import axiosInstance from '../axios';

interface AuthToken {
  exp: number;
  iat: number;
  id: number;
  role: string;
  username: string;
}

const REFRESH_THRESHOLD = 600; // access token 만료 시간을 얼마나 남겨둘지 결정하는 값(단위: 초)

/**
 * Axios 에 유저 Auth 인터셉터를 적용합니다.
 */
export const AxiosUserAuthGuard = ({ children }: { children: React.ReactNode }) => {
  const [done, setDone] = React.useState(false);

  const userAuth = useUserAuth();
  const setUserAuth = useSetUserAuth();
  const logoutUserAuth = useLogoutUserAuth();

  const userAuthRef = useRefValue(userAuth);

  React.useEffect(() => {
    if (userAuth?.userID) {
      analyticsService.setUser(userAuth.userID);
    }
  }, [userAuth?.userID]);

  React.useLayoutEffect(() => {
    const id = axiosInstance.interceptors.request.use(async config => {
      config.headers['request_id'] = uuidv4();

      // 토큰이 존재하지 않으면 토큰 재발행 로직 실행하지 않음
      const userAuth = userAuthRef.current;
      if (userAuth) {
        const token = jwtDecode<AuthToken>(userAuth.accessToken);

        const now = Date.now() / 1000;
        const expirationTime = token.exp;

        // 토큰이 만료되기 전에 미리 발행하는 로직
        if (expirationTime - now < REFRESH_THRESHOLD) {
          try {
            const response = await axios.post<{
              accessToken: string;
            }>(
              `${import.meta.env.VITE_PUBLIC_ACLOSET_API_URL}/shops/users/token`,
              {},
              {
                headers: {
                  request_id: uuidv4(),
                  Authorization: `Bearer ${userAuth.refreshToken}`,
                  'Content-Type': 'application/json',
                },
              },
            );

            setUserAuth(userAuth.userID, prev => ({
              ...prev,
              accessToken: response.data.accessToken,
            }));

            config.headers.Authorization = `Bearer ${response.data.accessToken}`;
          } catch (error) {
            acCaptureException('유저 토큰 refresh 실패', error);
            logoutUserAuth(userAuth.userID);
            window.location.href = '/login';
          }
        } else {
          config.headers.Authorization = `Bearer ${userAuth.accessToken}`;
        }
      }

      return config;
    });

    setDone(true);

    return () => {
      axiosInstance.interceptors.request.eject(id);
    };
  }, [setUserAuth, logoutUserAuth, userAuthRef]);

  return done ? children : null;
};
